<template>
  <DetailCard
    v-if="!isLoading"
    key="ek-bilgiler"
    title="Ek Bilgiler"
    cardKey="additional-info"
    :showUpdateButton="false"
    :showApplyToVariants="true"
    checkboxId="additional-info-apply-variants"
    v-model:applyToVariants="applyToVariants"
    @saveChanges="saveChanges"
    @requestUpdate="handleRequestUpdate"
    :isLoading="isLoading"
    :isSaving="isSaving"
    :isSaved="!hasChanges || showSuccessState"
    :showSuccessState="showSuccessState"
    :saveButtonWidth="showSuccessState ? 'auto' : '183px'"
    :class="{ disabled: isProductDisabled }"
  >
    <div class="radio-button-area d-flex flex-row" :class="{ disabled: isProductDisabled }">
      <div class="radio-button-wrapper d-flex flex-column">
        <div class="radio-button-info d-flex flex-column">
          <h3>{{ radioButtonsInfo[0].title }}</h3>
          <p>{{ radioButtonsInfo[0].text }}</p>
        </div>
        <div class="additional-info radio-button d-flex flex-column">
          <RadioButtonGroup
            :name="radioButtonsInfo[0].title"
            :options="radioButtonOptions"
            :disabled="isProductDisabled"
            @select="handleSelectRadio(radioButtonsInfo[0].id, $event)"
          >
            <span class="radio-button-label">Evet</span>
          </RadioButtonGroup>

          <div v-if="customProductOption" class="info-text" :class="{ disabled: isProductDisabled }">
            <Icons name="InfoFilledIcon" :color="isProductDisabled ? '#B4B4B4' : '#4FA4E5'" size="md" />
            <span
              >Kişiselleştirme seçeneklerinin her birini aşağıdaki alana tek tek
              girerek ekleyebilirsiniz. En fazla 3 seçenek sunabilirsiniz.</span
            >
          </div>

          <div v-if="customProductOption" class="input-area flex-row">
            <div class="input-area-with-info">
              <Input
                type="text"
                label="Ürün seçeneği yazın"
                :isError="customProductOptionErrorText"
                @focus="handleInputFocus"
                v-model:value="customProductOptionInput"
              />
              <p :class="{ 'error-text': customProductOptionErrorText }">
                {{ customProductOptionErrorText }}
              </p>
            </div>
            <Button
              primary
              class="input-area-button"
              :class="{
                'disabled': customProductOptionList.length >= 3,
              }"
              :disabled="
                isProductDisabled ||
                !customProductOptionInput ||
                customProductOptionList.length >= 3
              "
              @click="addToProductList()"
              >Ekle</Button
            >
          </div>

          <div v-if="customProductOption && customProductOptionList">
            <ul class="variants">
              <li v-for="item in customProductOptionList" :key="item.id">
                <span>{{ item.text }}</span>
                <Button
                  @click="removeFromProductList(item.id)"
                  link
                  class="remove-single-variant"
                  :disabled="isProductDisabled"
                >
                  <Icons name="Close" color="white" size="xs" />
                </Button>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div class="radio-button-wrapper d-flex flex-column">
        <div class="radio-button-info d-flex flex-column">
          <h3>{{ radioButtonsInfo[1].title }}</h3>
          <p>{{ radioButtonsInfo[1].text }}</p>
        </div>
        <div class="additional-info radio-button d-flex flex-column">
          <RadioButtonGroup
            :name="radioButtonsInfo[1].title"
            :options="radioButtonOptionsForQuantity"
            :disabled="isProductDisabled"
            @select="handleSelectRadio(radioButtonsInfo[1].id, $event)"
          >
            <span class="radio-button-label">Evet</span>
          </RadioButtonGroup>
          <div v-if="amountLimit" class="input-area flex-row">
            <div class="input-area-with-info">
              <Input
                type="number"
                :isOnlyNumber="true"
                label="En Fazla Satış Adedi"
                :isError="amountLimitErrorText"
                :disabled="isProductDisabled"
                @blur="checkError"
                @focus="resetSuccessState"
                v-model:value="maxSalesLimit"
              />
              <p :class="{ 'error-text': amountLimitErrorText }">
                {{ amountLimitErrorText }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </DetailCard>
  <ProductAdditionalInfoSkeleton v-else />
</template>

<script setup>
import { ref, reactive, computed, watch } from 'vue';
import { useStore } from 'vuex';
import DetailCard from './DetailCard.vue';
import { RadioButtonGroup, Input, Button, Icons } from "@n11/so-ui-components";
import { sendProductDetailEvent } from '@/utils/ga4/helper';
import { GA4_EVENT } from '@/utils/ga4/event';
import ProductAdditionalInfoSkeleton from '@/components/Skeleton/ProductDetailSkeleton/ProductAdditionalInfoSkeleton.vue'

const store = useStore();
const emit = defineEmits(['saveChanges', 'requestUpdate', 'hasChanges', 'saved']);

const props = defineProps({
  additionalInfo: {
    type: Object,
    default: () => ({}),
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const { customTextOptions, maxPurchaseQuantity } = props.additionalInfo;

const isProductDisabled = computed(() => store.getters['products/isProductDisabled']);

const customProductOption = ref(null);
const amountLimit = ref(null);
const customProductOptionInput = ref("");
const maxSalesLimit = ref(null);
const customProductOptionErrorText = ref(null);
const amountLimitErrorText = ref(null);
const customProductOptionList = ref([]);
const applyToVariants = ref(true);
const showSuccessState = ref(false);

const originalForm = ref({
  customProductOptionList: [],
  maxSalesLimit: null,
  customProductOption: null,
  amountLimit: null,
});

const isSaving = computed(() => {
  return store.getters['products/isProductAdditionalInfoLoading'];
});

const hasChanges = computed(() => {
  const currentCustomOptions = JSON.stringify(customProductOptionList.value);
  const originalCustomOptions = JSON.stringify(originalForm.value.customProductOptionList);
  
  const normalizeValue = (val) => {
    if (val === null || val === undefined || val === '') return null;
    return val;
  };
  
  const currentMaxSales = normalizeValue(maxSalesLimit.value);
  const originalMaxSales = normalizeValue(originalForm.value.maxSalesLimit);

  return (
    currentCustomOptions !== originalCustomOptions ||
    currentMaxSales !== originalMaxSales ||
    customProductOption.value !== originalForm.value.customProductOption ||
    amountLimit.value !== originalForm.value.amountLimit
  );
});

watch(hasChanges, (changed) => {
  if (changed) emit('hasChanges');
});

const radioButtonsInfo = ref([
  {
    id: 0,
    title: "Kişiselleştirilmiş ürün seçeneğiniz var mı?",
    text: "Müşterilerinize kişiselleştirilmiş bir ürün seçeneği sunup sunmadığınızı lütfen belirtin. (Örn: Kupa Üzerine İsim)",
  },
  {
    id: 1,
    title: "Ürünün satışında adet limiti var mı?",
    text: "Müşterinin bir siparişinde, ürün seçeneği bazında satın alabileceği adet limitiniz olup olmadığını lütfen belirtin.",
  },
]);

const radioButtonOptions = reactive([
  { id: "0", name: "Evet", selected: null },
  { id: "1", name: "Hayır", selected: null },
]);
const radioButtonOptionsForQuantity = reactive([
  { id: "0", name: "Evet", selected: null },
  { id: "1", name: "Hayır", selected: null },
]);

if (Array.isArray(customTextOptions) && customTextOptions.length > 0) {
  radioButtonOptions[0].selected = true;
  radioButtonOptions[1].selected = false;
  customProductOption.value = true;
  customProductOptionList.value = [...customTextOptions];
  originalForm.value.customProductOptionList = [...customTextOptions];
  originalForm.value.customProductOption = true;
} else {
  radioButtonOptions[0].selected = false;
  radioButtonOptions[1].selected = true;
  customProductOption.value = false;
  originalForm.value.customProductOption = false;
}

if (maxPurchaseQuantity) {
  radioButtonOptionsForQuantity[0].selected = true;
  radioButtonOptionsForQuantity[1].selected = false;
  amountLimit.value = true;
  maxSalesLimit.value = maxPurchaseQuantity;
  originalForm.value.maxSalesLimit = maxPurchaseQuantity;
  originalForm.value.amountLimit = true;
} else {
  radioButtonOptionsForQuantity[0].selected = false;
  radioButtonOptionsForQuantity[1].selected = true;
  amountLimit.value = false;
  originalForm.value.amountLimit = false;
}

watch(isSaving, (newValue, oldValue) => {
  if (oldValue === true && newValue === false) {
    const error = store.getters['products/getProductAdditionalInfoError'];
    if (!error) {
      originalForm.value.customProductOptionList = [...customProductOptionList.value];
      originalForm.value.maxSalesLimit = maxSalesLimit.value;
      originalForm.value.customProductOption = customProductOption.value;
      originalForm.value.amountLimit = amountLimit.value;
      showSuccessState.value = true;
      emit('saved');
    }
  }
});

watch(
  () => props.additionalInfo,
  (newValue) => {
    if (newValue) {
      const { customTextOptions: newCustomTextOptions, maxPurchaseQuantity: newMaxPurchase } = newValue;

      if (Array.isArray(newCustomTextOptions) && newCustomTextOptions.length > 0) {
        radioButtonOptions[0].selected = true;
        radioButtonOptions[1].selected = false;
        customProductOption.value = true;
        customProductOptionList.value = [...newCustomTextOptions];
        originalForm.value.customProductOptionList = [...newCustomTextOptions];
        originalForm.value.customProductOption = true;
      } else {
        radioButtonOptions[0].selected = false;
        radioButtonOptions[1].selected = true;
        customProductOption.value = false;
        customProductOptionList.value = [];
        originalForm.value.customProductOptionList = [];
        originalForm.value.customProductOption = false;
      }

      if (newMaxPurchase) {
        radioButtonOptionsForQuantity[0].selected = true;
        radioButtonOptionsForQuantity[1].selected = false;
        amountLimit.value = true;
        maxSalesLimit.value = newMaxPurchase;
        originalForm.value.maxSalesLimit = newMaxPurchase;
        originalForm.value.amountLimit = true;
      } else {
        radioButtonOptionsForQuantity[0].selected = false;
        radioButtonOptionsForQuantity[1].selected = true;
        amountLimit.value = false;
        maxSalesLimit.value = null;
        originalForm.value.maxSalesLimit = null;
        originalForm.value.amountLimit = false;
      }

      showSuccessState.value = false;
    }
  },
  { deep: true, immediate: true }
);

const resetSuccessState = () => {
  if (showSuccessState.value) {
    showSuccessState.value = false;
  }
};

const handleInputFocus = () => {
  if (customProductOptionList.value.length >= 3) {
    customProductOptionErrorText.value = "En fazla 3 seçenek sunabilirsiniz.";
  } else {
    customProductOptionErrorText.value = null;
  }
  resetSuccessState();
};

const handleSelectRadio = (questionId, selectedAnswer) => {
  resetSuccessState();

  if (questionId === 0) {
    customProductOption.value = selectedAnswer.id === "0" ? true : false;
    if (!customProductOption.value) {
      customProductOptionList.value = [];
    }
  } else if (questionId === 1) {
    amountLimit.value = selectedAnswer.id === "0" ? true : false;
    if (!amountLimit.value) {
      maxSalesLimit.value = null;
      amountLimitErrorText.value = null;
    }
  }
};

const removeFromProductList = (id) => {
  resetSuccessState();

  customProductOptionList.value = customProductOptionList.value.filter(
    (product) => product.id !== id
  );
  checkError();
};

const addToProductList = () => {
  if (!customProductOptionInput.value) return;

  resetSuccessState();
  
  if (
    customProductOptionList.value.find(
      (item) =>
        item.text.toLocaleLowerCase("tr-TR") ===
        customProductOptionInput.value.toLocaleLowerCase("tr-TR")
    )
  ) {
    store.commit("common/SET_TOASTER_DATA", {
      type: "error",
      title: "",
      description: "Bu seçenek daha önce eklenmiştir.",
    });
    customProductOptionErrorText.value = "";
    return;
  }
  customProductOptionList.value.push({
    id: customProductOptionList.value.length,
    text: customProductOptionInput.value,
  });
  customProductOptionInput.value = null;
  customProductOptionErrorText.value = null;
};

const checkError = () => {
  if (amountLimit.value && !maxSalesLimit.value)
    amountLimitErrorText.value = "Lütfen satış adedi limitini girin.";
  else if (parseInt(maxSalesLimit.value) > 999)
    amountLimitErrorText.value =
      "Ürünün satışında adet limiti maksimum 999 olabilir.";
  else amountLimitErrorText.value = null;
};

// const handleRequestUpdate = () => {
//   emit('requestUpdate');
// };

const saveChanges = async () => {
  const additionalInfo = getAdditionalInfo();
  if (additionalInfo.isValid) {
    const currentPersonalization = originalForm.value.customProductOption ? 'yes' : 'no';
    const currentLimit = originalForm.value.amountLimit ? 'yes' : 'no';
    const newPersonalization = customProductOption.value ? 'yes' : 'no';
    const newLimit = amountLimit.value ? 'yes' : 'no';

    try {
      const productId = props?.additionalInfo?.productIds;
      await store.dispatch("products/postProductAdditionalInfo", {
        productId,
        payload: additionalInfo.data,
      });

      sendProductDetailEvent(GA4_EVENT.SAVE_CHANGES, {
        button_name: 'Değişiklikleri Kaydet',
        button_type: 'Save Changes',
        button_area: 'Additional Information',
        item_id: productId,
        all_variants_checkbox: applyToVariants.value,
        current_status: `${currentPersonalization}|${currentLimit}`,
        new_status: `${newPersonalization}|${newLimit}`,
      });
      // emit('saveChanges', additionalInfo.data);
    } catch (error) {
      console.error("Ek bilgi kaydedilemedi:", error);
    }
  }
};

const getAdditionalInfo = () => {
  let customOptionIsValid = true;
  let amountLimitIsValid = true;

  if (customProductOption.value && customProductOptionList.value.length === 0) {
    customProductOptionErrorText.value =
      "Lütfen kişiselleştirilmiş ürün seçeneğinizi girin.";
    customOptionIsValid = false;
  } else customProductOptionErrorText.value = null;

  if (amountLimit.value && !maxSalesLimit.value) {
    amountLimitErrorText.value = "Lütfen satış adedi limitini girin.";
    amountLimitIsValid = false;
  } else if (maxSalesLimit.value && parseInt(maxSalesLimit.value) > 999) {
    amountLimitErrorText.value = "Ürünün satışında adet limiti maksimum 999 olabilir.";
    amountLimitIsValid = false;
  } else {
    amountLimitErrorText.value = null;
  }

  const hasCustomTextOptions =
    customProductOption.value === true &&
    customProductOptionList.value.length > 0;
  const hasMaxPurchaseQuantity =
    amountLimit.value === true && !!maxSalesLimit.value;

  const customTextOptionsArr = hasCustomTextOptions
    ? customProductOptionList.value.map((item) => item.text)
    : null;

  return {
    data: {
      hasCustomTextOptions,
      customTextOptions: customTextOptionsArr,
      hasMaxPurchaseQuantity,
      maxPurchaseQuantity: hasMaxPurchaseQuantity
        ? Number(maxSalesLimit.value)
        : null,
      changesAll: applyToVariants.value,
    },
    isValid: amountLimitIsValid && customOptionIsValid,
  };
};

const showSaveAllSuccessState = () => {
  originalForm.value.customProductOptionList = [...customProductOptionList.value];
  originalForm.value.maxSalesLimit = maxSalesLimit.value;
  originalForm.value.customProductOption = customProductOption.value;
  originalForm.value.amountLimit = amountLimit.value;
  showSuccessState.value = true;
};

defineExpose({
  getAdditionalInfo,
  showSaveAllSuccessState,
});
</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';
@import '@/assets/styles/utilities.scss';

.additional-info :deep(.radioButton-list) {
  flex-direction: row;
}

.additional-info :deep(.input-label) {
  pointer-events: none;
}

.info-text {
  border-radius: 6px;
  display: flex;
  gap: 12px;
  background: #e9f4fc;
  padding: 16px;
  max-width: 450px;

  span {
    font-size: 13px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
    letter-spacing: -0.05px;
    color: $dark-gray-5;
  }

  &.disabled {
    opacity: 0.5;

    span {
      color: $colorN60;
    }
  }
}

.radio-button-area {
  gap: 24px;
  position: relative;

  &.disabled {
    opacity: 0.8;
    pointer-events: none;
  }

  .radio-button-wrapper {
    flex: 1;
    gap: 12px;
    justify-content: space-between;
    height: max-content;

    &:not(:last-child) {
      border-right: 1px solid $colorD20;
    }

    .radio-button-info {
      gap: 6px;
      width: 92%;

      h3 {
        font-size: 14px;
        line-height: 18px;
        color: $colorN80;
        font-weight: 600;
      }

      p {
        font-size: 11px;
        line-height: 14px;
        color: $colorN80;
      }
    }

    .radio-button {
      gap: 12px;
      cursor: pointer;

      .radio-button-label {
        font-size: 12px;
        line-height: 16px;
        color: $colorN80;
      }
    }
  }
}

.input-area {
  display: flex;
  flex-direction: row;
  max-width: 450px;
  gap: 8px;

  &-with-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;

    p {
      font-size: 11px;
      line-height: 14px;
      padding-left: 12px;
      color: $colorN60;
    }

    .error-text {
      color: $colorE50;
    }
  }

  &-button {
    padding: 11px 20px !important;
    border-radius: 6px;
    font-size: 14px;
    line-height: 18px;
    height: max-content;
    width: max-content !important;
  }

  .disabled-button {
    border: transparent;
  }
}

.variants {
  display: flex;
  flex-flow: wrap;
  gap: 4px;
  max-width: 384px;

  li {
    border: 1px solid $colorN40;
    border-radius: 20px;
    background-color: $colorN00;
    padding: 6px 16px;
    display: inline-flex;
    align-items: center;
    gap: 4px;

    font-family: Open Sans;
    font-weight: 400;
    font-size: 13px;
    line-height: 18px;
    letter-spacing: -0.05px;
  }

  .remove-single-variant {
    color: $colorM50;
    font-family: Open Sans;
    font-weight: 600;
    font-size: 14px;
    line-height: 18px;
    letter-spacing: -0.05px;
    text-decoration: none;
    background-color: $colorN30 !important;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    padding: 4px;
    display: flex;
    align-items: center;
    flex-direction: column;
    transition: background-color ease-in-out 150ms;

    &:hover {
      background-color: $colorN70 !important;
    }
  }
}

:deep(.section.disabled) {
  .apply-variants {
    pointer-events: none;
    opacity: 0.6;

    input[type="checkbox"] {
      opacity: 0.6;
      cursor: not-allowed;
    }

    label {
      opacity: 0.6;
      cursor: not-allowed;
      color: $colorN60;
    }
  }

  .actions-row {
    button {
      opacity: 0.6;
      cursor: not-allowed;
      pointer-events: none;
    }
  }
}
</style>
